import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

export interface PetInfo {
  id: string;
  taskId: number;
  petName: string;
  petType: string;
  breed: string;
  age: number;
  weight: number;
  color: string;
  gender: string;
  microchipNumber: string;
  registrationNumber: string;
  veterinarianName: string;
  veterinarianPhone: string;
  veterinarianAddress: string;
  emergencyVetName: string;
  emergencyVetPhone: string;
  medicalConditions: string;
  medications: string;
  allergies: string;
  feedingInstructions: string;
  specialNeeds: string;
  behavioralNotes: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  additionalData?: any;
  visibilityState: 'LOCKED' | 'UNLOCKED';
}

export interface CreatePetInfoData {
  taskId: number;
  petName: string;
  petType: string;
  breed: string;
  age: number;
  weight: number;
  color: string;
  gender: string;
  microchipNumber: string;
  registrationNumber: string;
  veterinarianName: string;
  veterinarianPhone: string;
  veterinarianAddress: string;
  emergencyVetName: string;
  emergencyVetPhone: string;
  medicalConditions: string;
  medications: string;
  allergies: string;
  feedingInstructions: string;
  specialNeeds: string;
  behavioralNotes: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  additionalData?: any;
}

export interface UpdatePetInfoData extends CreatePetInfoData {
  visibilityState?: 'LOCKED' | 'UNLOCKED';
}

class PetInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getPetInfo(taskId: number): Promise<PetInfo[]> {
    const response = await this.makeRequest(`/api/pet-info?taskId=${taskId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch pet info: ${response.status}`);
    }
    
    return await response.json();
  }

  async createPetInfo(data: CreatePetInfoData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/pet-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create pet info: ${response.status}`);
    }
    
    return await response.json();
  }

  async updatePetInfo(id: string, data: UpdatePetInfoData): Promise<PetInfo> {
    const response = await this.makeRequest(`/api/pet-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update pet info: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async deletePetInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/pet-info/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete pet info: ${response.status}`);
    }
  }

  async setVisibilityState(id: string, visibilityState: 'LOCKED' | 'UNLOCKED'): Promise<PetInfo> {
    const response = await this.makeRequest(`/api/pet-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ visibilityState }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update visibility state: ${response.status}`);
    }
    
    return await response.json();
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest(`/api/pet-info/complete/${taskId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark pet info as completed: ${response.status}`);
    }

    return await response.json();
  }

  async lockPetInfo(petId: string): Promise<void> {
    const response = await this.makeRequest(`/api/pet-info/${petId}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock pet information: ${response.status}`);
    }
  }

  async unlockPetInfo(petId: string, emails?: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/pet-info/${petId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ emails: emails || [] }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock pet information: ${response.status}`);
    }
  }

  async getAccessGrants(petId: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/pet-info/${petId}/access-grants`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get access grants: ${response.status}`);
    }

    return await response.json();
  }

  async getAllPetInfo(targetUserId?: string): Promise<PetInfo[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await this.makeRequest(`/api/pet-info${params}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch all pet info: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }
}

export const petInfoService = new PetInfoService();